import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useJobData } from "@/hooks/useJobData";
import { useJobTrades } from "@/hooks/useJobTrades";
import { useFileUpload } from "@/hooks/useFileUpload";
import { useJobUpdate } from "@/hooks/useJobUpdate";
import { useDuplicateJob } from "@/hooks/useDuplicateJob";
import { useArchiveJob } from "@/hooks/useArchiveJob";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Loader2, Trash2, CalendarIcon, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { AddressAutocomplete } from "@/components/measurements/AddressAutocomplete";
import { useGoogleMaps } from "@/hooks/useGoogleMaps";
import { z } from "zod";
import { Textarea } from "@/components/ui/textarea";
import { CreateProposalDialog } from "@/components/proposals/CreateProposalDialog";
import { JobHeader } from "@/components/job-detail/JobHeader";
import { WorkflowBar } from "@/components/job-detail/WorkflowBar";
import { JobTabs } from "@/components/job-detail/JobTabs";
import { JobTradesOverview } from "@/components/job-detail/JobTradesOverview";
import { TradeDetailView } from "@/components/job-detail/TradeDetailView";
import { OverviewTab } from "@/components/job-detail/OverviewTab";
import { EstimatesTab } from "@/components/job-detail/EstimatesTab";
import { ProposalsTab } from "@/components/job-detail/ProposalsTab";
import { ContingencyFormsTab } from "@/components/job-detail/ContingencyFormsTab";
import { ClaimReaderTab } from "@/components/job-detail/ClaimReaderTab";
import { CommercialTab } from "@/components/job-detail/CommercialTab";
import { JobFormsTab } from "@/components/job-detail/JobFormsTab";
import { SupplementsTab } from "@/components/job-detail/SupplementsTab";
import { ProductionTab } from "@/components/job-detail/ProductionTab";
import { JobCommunications } from "@/components/job-detail/JobCommunications";
import { DamageHubTab } from "@/components/job-detail/DamageHubTab";
import { JobDocumentsSheet } from "@/components/job-detail/JobDocumentsSheet";
import { JobTasksSheet } from "@/components/job-detail/JobTasksSheet";
import { JobPaymentsSheet } from "@/components/job-detail/JobPaymentsSheet";
import { JobTimelineSheet } from "@/components/job-detail/JobTimelineSheet";
import { CollaborationFeed } from "@/components/collaboration/CollaborationFeed";
import { CommentComposer } from "@/components/collaboration/CommentComposer";
import { ApprovalRequestDialog } from "@/components/collaboration/ApprovalRequestDialog";
import { CheckCircle } from "lucide-react";
import { MobileJobHeader } from "@/components/job-detail/MobileJobHeader";
import { MobileJobTabs } from "@/components/job-detail/MobileJobTabs";
import { MobileActionsSheet } from "@/components/job-detail/MobileActionsSheet";
import { useIsMobile } from "@/hooks/use-mobile";

interface Job {
  id: string;
  job_number: string;
  status: string;
  property_address: string;
  property_city: string;
  property_state: string;
  property_zip: string;
  owner_name: string;
  owner_email: string;
  owner_phone: string;
  insurance_company: string;
  claim_number: string;
  adjuster_name: string;
  adjuster_phone: string;
  adjuster_email: string;
  policy_number: string;
  roof_type: string;
  roof_age: number;
  estimated_value: number;
  notes: string;
  created_at: string;
  updated_at: string;
  property_type: string;
  latitude: number;
  longitude: number;
  org_id: string;
}

const jobEditSchema = z.object({
  owner_name: z.string().trim().min(1, "Job name is required").max(200, "Job name too long"),
  property_address: z.string().trim().min(1, "Address is required").max(500, "Address too long"),
  property_type: z.string().min(1, "Job type is required")
});

interface Photo {
  id: string;
  url: string;
  filename: string;
  storage_path: string;
  ai_analyzed: boolean;
  ai_damage_detected: boolean;
  ai_damage_types: string[];
  ai_confidence_score: number;
  ai_analysis_result: any;
  created_at: string;
}

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Use centralized auth and data hooks
  const { user, orgId, loading: authLoading } = useAuth();
  const { job, photos, documents, tasks, timeline, estimates, loading: dataLoading, refetch: refetchJob } = useJobData(id || '');
  const { data: jobTrades = [], refetch: refetchTrades } = useJobTrades(id, orgId);
  const { uploadFile, uploading } = useFileUpload(orgId || '');
  const { duplicateJob } = useDuplicateJob();
  const { archiveJob } = useArchiveJob();
  
  // Additional state not covered by hooks
  const [measurements, setMeasurements] = useState<any>({ roof: [], siding: [], gutters: [] });
  const [notes, setNotes] = useState<any[]>([]);
  const [proposals, setProposals] = useState<any[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [selectedTradeId, setSelectedTradeId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("Overview");
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const { isLoaded } = useGoogleMaps();
  
  // Sheet states for secondary features
  const [documentsSheetOpen, setDocumentsSheetOpen] = useState(false);
  const [tasksSheetOpen, setTasksSheetOpen] = useState(false);
  const [paymentsSheetOpen, setPaymentsSheetOpen] = useState(false);
  const [timelineSheetOpen, setTimelineSheetOpen] = useState(false);
  const [contingencyFormsDialogOpen, setContingencyFormsDialogOpen] = useState(false);
  const [policyScannerDialogOpen, setPolicyScannerDialogOpen] = useState(false);
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  
  const loading = authLoading || dataLoading;
  const isMobile = useIsMobile();

  // Handle URL tab switching (for Push to Proposal, etc.)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
      // Clear the tab param after setting to avoid issues on refresh
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('tab');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Edit form state
  const [editName, setEditName] = useState("");
  const [editAddress, setEditAddress] = useState("");
  const [editLat, setEditLat] = useState<number | null>(null);
  const [editLng, setEditLng] = useState<number | null>(null);
  const [editType, setEditType] = useState("");
  const [editOwnerEmail, setEditOwnerEmail] = useState("");
  const [editOwnerPhone, setEditOwnerPhone] = useState("");
  const [editInsuranceCompany, setEditInsuranceCompany] = useState("");
  const [editClaimNumber, setEditClaimNumber] = useState("");
  const [editAdjusterName, setEditAdjusterName] = useState("");
  const [editAdjusterPhone, setEditAdjusterPhone] = useState("");
  const [editAdjusterEmail, setEditAdjusterEmail] = useState("");
  const [editPolicyNumber, setEditPolicyNumber] = useState("");
  const [editRoofType, setEditRoofType] = useState("");
  const [editRoofAge, setEditRoofAge] = useState("");
  const [editInsuranceOpen, setEditInsuranceOpen] = useState(false);
  const [editPropertyOpen, setEditPropertyOpen] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Task form state
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDescription, setTaskDescription] = useState("");
  const [taskPriority, setTaskPriority] = useState("medium");
  const [taskDueDate, setTaskDueDate] = useState<Date>();

  // Note form state
  const [noteContent, setNoteContent] = useState("");

  // Load additional data not covered by useJobData hook
  useEffect(() => {
    if (!id || !orgId) return;
    
    const loadAdditionalData = async () => {
      const [
        { data: roofData },
        { data: sidingData },
        { data: guttersData },
        { data: notesData },
        { data: proposalsData },
        { data: paymentsData }
      ] = await Promise.all([
        supabase.from('measurements_roof').select('*').eq('job_id', id).eq('org_id', orgId),
        supabase.from('measurements_siding').select('*').eq('job_id', id).eq('org_id', orgId),
        supabase.from('measurements_gutters').select('*').eq('job_id', id).eq('org_id', orgId),
        supabase.from('notes').select('*').eq('job_id', id).eq('org_id', orgId).order('created_at', { ascending: false }),
        supabase.from('proposal_documents').select('*').eq('job_id', id).eq('org_id', orgId).order('created_at', { ascending: false }),
        supabase.from('payments').select('*').eq('job_id', id).eq('org_id', orgId).order('created_at', { ascending: false })
      ]);

      setMeasurements({
        roof: roofData || [],
        siding: sidingData || [],
        gutters: guttersData || []
      });
      setNotes(notesData || []);
      setProposals(proposalsData || []);
      setPayments(paymentsData || []);
    };

    loadAdditionalData();
  }, [id, orgId]);


  // Listen for Bridgette tab change events from Global CoPilot
  useEffect(() => {
    const handleTabChange = (event: CustomEvent<{ tab: string }>) => {
      console.log("📥 JobDetail received tab change event:", event.detail.tab);
      setActiveTab(event.detail.tab);
    };

    const handleOpenForm = (event: CustomEvent<{ formType: string }>) => {
      console.log("📥 JobDetail received open form event:", event.detail.formType);
      const { formType } = event.detail;
      
      // Map form types to the appropriate dialog/sheet state
      switch (formType) {
        case 'create_task':
          setTaskDialogOpen(true);
          break;
        case 'add_note':
          setNoteDialogOpen(true);
          break;
        case 'create_proposal':
          setProposalDialogOpen(true);
          break;
        case 'upload_photo':
          fileInputRef.current?.click();
          break;
        case 'contingency_forms':
          setContingencyFormsDialogOpen(true);
          break;
        case 'policy_scanner':
          setPolicyScannerDialogOpen(true);
          break;
        case 'edit_job':
          // Pre-populate form with current job data
          if (job) {
            setEditName(job.owner_name);
            setEditAddress(job.property_address);
            setEditType(job.property_type);
            setEditLat(job.latitude);
            setEditLng(job.longitude);
            setEditOwnerEmail(job.owner_email || "");
            setEditOwnerPhone(job.owner_phone || "");
            setEditInsuranceCompany(job.insurance_company || "");
            setEditClaimNumber(job.claim_number || "");
            setEditAdjusterName(job.adjuster_name || "");
            setEditAdjusterPhone(job.adjuster_phone || "");
            setEditAdjusterEmail(job.adjuster_email || "");
            setEditPolicyNumber(job.policy_number || "");
            setEditRoofType(job.roof_type || "");
            setEditRoofAge(job.roof_age?.toString() || "");
          }
          setEditDialogOpen(true);
          break;
        default:
          console.warn("Unknown form type:", formType);
      }
    };

    window.addEventListener('bridgette-change-tab', handleTabChange as EventListener);
    window.addEventListener('bridgette-open-form', handleOpenForm as EventListener);
    
    return () => {
      window.removeEventListener('bridgette-change-tab', handleTabChange as EventListener);
      window.removeEventListener('bridgette-open-form', handleOpenForm as EventListener);
    };
  }, [job]);

  useEffect(() => {
    if (id) {
      // Listen for estimate-created events from measurements
      const handleEstimateCreated = (event: any) => {
        if (event.detail?.jobId === id) {
          toast.success("New estimate added from measurement!", {
            description: "Check the Estimates tab to review",
            action: {
              label: "View",
              onClick: () => setActiveTab("Estimates"),
            },
          });
          refetchJob();
        }
      };

      window.addEventListener('estimate-created', handleEstimateCreated as EventListener);
      return () => window.removeEventListener('estimate-created', handleEstimateCreated as EventListener);
    }
  }, [id, refetchJob]);

  const { updateJobStatus } = useJobUpdate();
  
  // Normalize property type to workflow type (handles case variations)
  const normalizePropertyType = (type: string): 'retail' | 'insurance' | 'commercial' => {
    const normalized = type?.toLowerCase().trim();
    if (normalized === 'retail' || normalized === 'residential') return 'retail';
    if (normalized === 'insurance') return 'insurance';
    return 'commercial';
  };
  
  const handleStatusChange = async (newStatus: string) => {
    if (!job || !id) return;
    
    const workflowType = normalizePropertyType(job.property_type);
    
    const result = await updateJobStatus(id, newStatus, workflowType);
    
    if (result.success) {
      await refetchJob();
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    try {
      if (!user || !orgId) {
        toast.error("You must be logged in to upload photos");
        return;
      }

      for (const file of Array.from(files)) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${orgId}/${id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('job-photos')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('job-photos')
          .getPublicUrl(filePath);

        const { error: dbError } = await supabase.from('photos').insert({
          job_id: id,
          org_id: orgId,
          filename: file.name,
          storage_path: filePath,
          url: publicUrl,
          uploaded_by: user.id
        });

        if (dbError) throw dbError;
      }

      toast.success(`Uploaded ${files.length} photo(s)`);
      await refetchJob();
    } catch (error: any) {
      console.error("Error uploading photos:", error);
      toast.error(error.message || "Failed to upload photos");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId: string, storagePath: string) => {
    try {
      const { error: storageError } = await supabase.storage
        .from('job-photos')
        .remove([storagePath]);

      if (storageError) throw storageError;

      const { error: dbError } = await supabase
        .from('photos')
        .delete()
        .eq('id', photoId);

      if (dbError) throw dbError;

      toast.success("Photo deleted");
      await refetchJob();
    } catch (error: any) {
      console.error("Error deleting photo:", error);
      toast.error("Failed to delete photo");
    }
  };

  const handlePlaceSelected = (place: { address: string; lat: number; lng: number }) => {
    setEditAddress(place.address);
    setEditLat(place.lat);
    setEditLng(place.lng);
  };

  const handleSaveEdit = async () => {
    setEditErrors({});
    try {
      jobEditSchema.parse({
        owner_name: editName,
        property_address: editAddress,
        property_type: editType
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setEditErrors(errors);
        return;
      }
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('jobs')
        .update({
          owner_name: editName,
          owner_email: editOwnerEmail || null,
          owner_phone: editOwnerPhone || null,
          property_address: editAddress,
          property_type: editType,
          latitude: editLat,
          longitude: editLng,
          insurance_company: editInsuranceCompany || null,
          claim_number: editClaimNumber || null,
          adjuster_name: editAdjusterName || null,
          adjuster_phone: editAdjusterPhone || null,
          adjuster_email: editAdjusterEmail || null,
          policy_number: editPolicyNumber || null,
          roof_type: editRoofType || null,
          roof_age: editRoofAge ? parseInt(editRoofAge) : null,
        })
        .eq('id', id);

      if (error) throw error;

      toast.success("Job updated successfully");
      setEditDialogOpen(false);
      await refetchJob();
    } catch (error: any) {
      console.error("Error updating job:", error);
      toast.error(error.message || "Failed to update job");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTask = async () => {
    if (!taskTitle.trim()) return;

    try {
      if (!user || !orgId) return;

      const { error } = await supabase.from('tasks').insert({
        job_id: id,
        org_id: orgId,
        title: taskTitle,
        description: taskDescription,
        priority: taskPriority,
        due_date: taskDueDate?.toISOString(),
        status: 'pending',
        created_by: user.id
      });

      if (error) throw error;

      toast.success("Task created");
      setTaskDialogOpen(false);
      setTaskTitle("");
      setTaskDescription("");
      setTaskPriority("medium");
      setTaskDueDate(undefined);
      await refetchJob();
    } catch (error: any) {
      console.error("Error creating task:", error);
      toast.error("Failed to create task");
    }
  };

  const handleAddNote = () => {
    setNoteDialogOpen(true);
  };

  const handleCreateNote = async () => {
    if (!noteContent.trim()) return;

    try {
      if (!user || !orgId) return;

      const { error } = await supabase.from('notes').insert({
        job_id: id,
        org_id: orgId,
        content: noteContent,
        created_by: user.id
      });

      if (error) throw error;

      toast.success("Note created");
      setNoteDialogOpen(false);
      setNoteContent("");
      await refetchJob();
    } catch (error: any) {
      console.error("Error creating note:", error);
      toast.error("Failed to create note");
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error("Please log in to view this job");
      navigate('/auth');
    }
  }, [authLoading, user, navigate]);

  if (loading || !job) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-background pb-16 md:pb-0">
        {/* Mobile Header */}
        {isMobile ? (
          <>
            <MobileJobHeader
              job={job}
              onMenuOpen={() => setMobileActionsOpen(true)}
            />
            <WorkflowBar 
              currentStatus={job.status} 
              jobType={job.property_type}
              jobId={job.id}
              onStatusChange={handleStatusChange} 
            />
          </>
        ) : (
          <>
            <JobHeader
              job={job}
              photoCount={photos.length}
              documentCount={documents.length}
              taskCount={tasks.length}
              onTabChange={setActiveTab}
              onEditJob={() => {
                setEditName(job.owner_name);
                setEditAddress(job.property_address);
                setEditType(job.property_type);
                setEditLat(job.latitude);
                setEditLng(job.longitude);
                setEditOwnerEmail(job.owner_email || "");
                setEditOwnerPhone(job.owner_phone || "");
                setEditInsuranceCompany(job.insurance_company || "");
                setEditClaimNumber(job.claim_number || "");
                setEditAdjusterName(job.adjuster_name || "");
                setEditAdjusterPhone(job.adjuster_phone || "");
                setEditAdjusterEmail(job.adjuster_email || "");
                setEditPolicyNumber(job.policy_number || "");
                setEditRoofType(job.roof_type || "");
                setEditRoofAge(job.roof_age?.toString() || "");
                setEditDialogOpen(true);
              }}
              onDuplicateJob={() => toast.info("Duplicate job feature coming soon")}
              onArchiveJob={() => toast.info("Archive job feature coming soon")}
              onOpenDocuments={() => setDocumentsSheetOpen(true)}
              onOpenTasks={() => setTasksSheetOpen(true)}
              onOpenPayments={() => setPaymentsSheetOpen(true)}
              onOpenTimeline={() => setTimelineSheetOpen(true)}
            />
            <WorkflowBar 
              currentStatus={job.status} 
              jobType={job.property_type}
              jobId={job.id}
              onStatusChange={handleStatusChange} 
            />
            <JobTabs activeTab={activeTab} onTabChange={setActiveTab} jobType={job.property_type} />
          </>
        )}

        {activeTab === "Overview" && !selectedTradeId && (
          <>
            <div className="container mx-auto px-4 md:px-6 py-6">
              <JobTradesOverview
                jobId={id!}
                orgId={job.org_id}
                jobTrades={jobTrades}
                propertyType={job.property_type}
                onRefresh={refetchJob}
                onSelectTrade={setSelectedTradeId}
              />
            </div>
            <OverviewTab
              job={job}
              photos={photos}
              measurements={measurements}
              notes={notes}
              tasks={tasks}
              documents={documents}
              jobTrades={jobTrades}
              onAddNote={handleAddNote}
              onAddTask={() => setTaskDialogOpen(true)}
              onTabChange={setActiveTab}
            />
        </>
      )}

      {activeTab === "Overview" && selectedTradeId && (
        <TradeDetailView
          jobId={id}
          orgId={job.org_id}
          tradeId={selectedTradeId}
          jobTrade={jobTrades?.find(jt => jt.id === selectedTradeId)}
          propertyType={job.property_type || "Retail"}
          jobData={job}
          jobAddress={job.property_address}
          onBack={() => setSelectedTradeId(null)}
          onRefresh={refetchJob}
        />
      )}

      {activeTab === "Photo Hub" && (
          <DamageHubTab
            jobId={id!}
            orgId={job.org_id}
            photos={photos}
            onPhotosUpdate={refetchJob}
            onFindingsUpdate={refetchJob}
          />
        )}

        {activeTab === "Measurement/Estimate" && job.property_type !== "Commercial" && (
          <EstimatesTab
            key={`estimates-${Date.now()}`}
            jobId={id!}
          />
        )}

        {activeTab === "Quick Estimate" && job.property_type === "Commercial" && (
          <div className="container mx-auto px-6 py-6">
            <CommercialTab jobId={id!} orgId={job.org_id} />
          </div>
        )}

        {activeTab === "Proposals" && job.property_type !== "Insurance" && (
          <ProposalsTab
            jobId={id!}
            proposals={proposals}
            jobData={job}
            onCreateProposal={() => setProposalDialogOpen(true)}
          />
        )}

        {activeTab === "Forms" && (
          <div className="container mx-auto px-4 md:px-6 py-6">
            <JobFormsTab jobId={id!} />
          </div>
        )}

        {activeTab === "Supplements" && job.property_type === "Insurance" && (
          <SupplementsTab jobId={id!} orgId={job.org_id} />
        )}

        {activeTab === "Production" && (
          <ProductionTab
            jobId={id!}
            orgId={job.org_id}
            jobStatus={job.status}
            onChecklistComplete={() => {
              toast.success("Quality checks complete!", {
                description: "Job is ready to be invoiced",
              });
              refetchJob();
            }}
          />
        )}

        {activeTab === "Team & Activity" && (
          <div className="container mx-auto px-4 md:px-6 py-6">
            <div className="max-w-4xl mx-auto space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Team Collaboration</h2>
                  <p className="text-muted-foreground">Comments, mentions, and approval requests</p>
                </div>
                <ApprovalRequestDialog
                  jobId={id!}
                  orgId={job.org_id}
                >
                  <Button variant="outline" size="sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Request Approval
                  </Button>
                </ApprovalRequestDialog>
              </div>

              <CollaborationFeed
                jobId={id!}
                orgId={job.org_id}
              />

              <CommentComposer
                jobId={id!}
                orgId={job.org_id}
                onCommentAdded={refetchJob}
              />
            </div>
          </div>
        )}

        {activeTab === "Contingency Forms" && job.property_type === "Insurance" && (
          <div className="px-4 md:px-0">
            <ContingencyFormsTab jobId={id!} orgId={job.org_id} />
          </div>
        )}

        {activeTab === "Communications" && (
          <div className="container mx-auto px-4 md:px-6 py-4 md:py-6">
            <JobCommunications
              jobId={id!}
              customerEmail={job.owner_email}
              customerName={job.owner_name}
              customerPhone={job.owner_phone}
              onAddNote={() => setNoteDialogOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Hidden Dialogs */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Job Details</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Contact Name</Label>
              <Input 
                id="edit-name" 
                value={editName} 
                onChange={e => setEditName(e.target.value)} 
                placeholder="Enter contact name" 
              />
              {editErrors.owner_name && <p className="text-sm text-destructive">{editErrors.owner_name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-address">Property Address</Label>
              <AddressAutocomplete 
                value={editAddress} 
                onChange={setEditAddress} 
                onPlaceSelected={handlePlaceSelected} 
                isLoaded={isLoaded} 
              />
              {editErrors.property_address && <p className="text-sm text-destructive">{editErrors.property_address}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-owner-email">Email</Label>
                <Input 
                  id="edit-owner-email" 
                  type="email"
                  value={editOwnerEmail} 
                  onChange={e => setEditOwnerEmail(e.target.value)} 
                  placeholder="john@example.com" 
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-owner-phone">Phone</Label>
                <Input 
                  id="edit-owner-phone" 
                  value={editOwnerPhone} 
                  onChange={e => setEditOwnerPhone(e.target.value)} 
                  placeholder="(555) 123-4567" 
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-type">Job Type</Label>
              <Select value={editType} onValueChange={setEditType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select job type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Retail">Retail</SelectItem>
                  <SelectItem value="Insurance">Insurance</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                </SelectContent>
              </Select>
              {editErrors.property_type && <p className="text-sm text-destructive">{editErrors.property_type}</p>}
            </div>

            {/* Insurance Fields - Collapsible */}
            {editType === "Insurance" && (
              <Collapsible open={editInsuranceOpen} onOpenChange={setEditInsuranceOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" className="w-full justify-between" type="button">
                    <span className="font-medium">Insurance Details (Optional)</span>
                    <ChevronDown className={cn("h-4 w-4 transition-transform", editInsuranceOpen && "rotate-180")} />
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-insurance-company">Insurance Company</Label>
                      <Input
                        id="edit-insurance-company"
                        value={editInsuranceCompany}
                        onChange={(e) => setEditInsuranceCompany(e.target.value)}
                        placeholder="State Farm"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-claim-number">Claim Number</Label>
                      <Input
                        id="edit-claim-number"
                        value={editClaimNumber}
                        onChange={(e) => setEditClaimNumber(e.target.value)}
                        placeholder="CLM-123456"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-policy-number">Policy Number</Label>
                    <Input
                      id="edit-policy-number"
                      value={editPolicyNumber}
                      onChange={(e) => setEditPolicyNumber(e.target.value)}
                      placeholder="POL-789012"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-adjuster-name">Adjuster Name</Label>
                    <Input
                      id="edit-adjuster-name"
                      value={editAdjusterName}
                      onChange={(e) => setEditAdjusterName(e.target.value)}
                      placeholder="Jane Doe"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="edit-adjuster-phone">Adjuster Phone</Label>
                      <Input
                        id="edit-adjuster-phone"
                        value={editAdjusterPhone}
                        onChange={(e) => setEditAdjusterPhone(e.target.value)}
                        placeholder="(555) 987-6543"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="edit-adjuster-email">Adjuster Email</Label>
                      <Input
                        id="edit-adjuster-email"
                        type="email"
                        value={editAdjusterEmail}
                        onChange={(e) => setEditAdjusterEmail(e.target.value)}
                        placeholder="adjuster@insurance.com"
                      />
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {/* Property Details - Collapsible */}
            <Collapsible open={editPropertyOpen} onOpenChange={setEditPropertyOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between" type="button">
                  <span className="font-medium">Property Details (Optional)</span>
                  <ChevronDown className={cn("h-4 w-4 transition-transform", editPropertyOpen && "rotate-180")} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-4 pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-roof-type">Roof Type</Label>
                    <Select value={editRoofType} onValueChange={setEditRoofType}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Asphalt Shingle">Asphalt Shingle</SelectItem>
                        <SelectItem value="Metal">Metal</SelectItem>
                        <SelectItem value="Tile">Tile</SelectItem>
                        <SelectItem value="Flat">Flat</SelectItem>
                        <SelectItem value="TPO">TPO</SelectItem>
                        <SelectItem value="EPDM">EPDM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-roof-age">Roof Age (years)</Label>
                    <Input
                      id="edit-roof-age"
                      type="number"
                      value={editRoofAge}
                      onChange={(e) => setEditRoofAge(e.target.value)}
                      placeholder="10"
                      min="0"
                    />
                  </div>
                </div>
              </CollapsibleContent>
            </Collapsible>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveEdit} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Task Title</Label>
              <Input 
                value={taskTitle} 
                onChange={e => setTaskTitle(e.target.value)} 
                placeholder="e.g., Schedule inspection" 
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={taskDescription} 
                onChange={e => setTaskDescription(e.target.value)} 
                placeholder="Add details..." 
              />
            </div>

            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={taskPriority} onValueChange={setTaskPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Due Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !taskDueDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {taskDueDate ? format(taskDueDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar 
                    mode="single" 
                    selected={taskDueDate} 
                    onSelect={setTaskDueDate} 
                    initialFocus 
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} disabled={!taskTitle}>Create Task</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Note Content</Label>
              <Textarea
                value={noteContent}
                onChange={(e) => setNoteContent(e.target.value)}
                placeholder="Add your note here..."
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateNote} disabled={!noteContent}>
                Create Note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <CreateProposalDialog
        open={proposalDialogOpen} 
        onOpenChange={setProposalDialogOpen}
        jobId={id!}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        className="hidden"
        onChange={handleFileUpload}
      />

      {/* Sheet Panels for Secondary Features */}
      <JobDocumentsSheet
        open={documentsSheetOpen}
        onOpenChange={setDocumentsSheetOpen}
        jobId={id!}
        documents={documents}
        onDocumentsUpdate={refetchJob}
      />

      <JobTasksSheet
        open={tasksSheetOpen}
        onOpenChange={setTasksSheetOpen}
        tasks={tasks}
        onAddTask={() => setTaskDialogOpen(true)}
        onTaskUpdate={refetchJob}
      />

      <JobPaymentsSheet
        open={paymentsSheetOpen}
        onOpenChange={setPaymentsSheetOpen}
        jobId={id!}
        payments={payments}
        onPaymentsUpdate={refetchJob}
      />

      <JobTimelineSheet
        open={timelineSheetOpen}
        onOpenChange={setTimelineSheetOpen}
        timeline={timeline}
      />

      {/* Insurance-specific Dialogs */}
      {contingencyFormsDialogOpen && (
        <Dialog open={contingencyFormsDialogOpen} onOpenChange={setContingencyFormsDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <ContingencyFormsTab jobId={id!} orgId={job.org_id} />
          </DialogContent>
        </Dialog>
      )}

      {policyScannerDialogOpen && (
        <Dialog open={policyScannerDialogOpen} onOpenChange={setPolicyScannerDialogOpen}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <ClaimReaderTab jobId={id!} />
          </DialogContent>
        </Dialog>
      )}

      {/* Mobile Actions Sheet */}
      {isMobile && (
        <MobileActionsSheet
          open={mobileActionsOpen}
          onOpenChange={setMobileActionsOpen}
          currentStatus={job.status}
          onStatusChange={handleStatusChange}
          onEditJob={() => {
            setEditName(job.owner_name);
            setEditAddress(job.property_address);
            setEditType(job.property_type);
            setEditLat(job.latitude);
            setEditLng(job.longitude);
            setEditOwnerEmail(job.owner_email || "");
            setEditOwnerPhone(job.owner_phone || "");
            setEditInsuranceCompany(job.insurance_company || "");
            setEditClaimNumber(job.claim_number || "");
            setEditAdjusterName(job.adjuster_name || "");
            setEditAdjusterPhone(job.adjuster_phone || "");
            setEditAdjusterEmail(job.adjuster_email || "");
            setEditPolicyNumber(job.policy_number || "");
            setEditRoofType(job.roof_type || "");
            setEditRoofAge(job.roof_age?.toString() || "");
            setEditDialogOpen(true);
            setMobileActionsOpen(false);
          }}
          onDuplicateJob={() => {
            toast.info("Duplicate job feature coming soon");
            setMobileActionsOpen(false);
          }}
          onArchiveJob={() => {
            toast.info("Archive job feature coming soon");
            setMobileActionsOpen(false);
          }}
          onOpenDocuments={() => {
            setDocumentsSheetOpen(true);
            setMobileActionsOpen(false);
          }}
          onOpenTasks={() => {
            setTasksSheetOpen(true);
            setMobileActionsOpen(false);
          }}
          onOpenPayments={() => {
            setPaymentsSheetOpen(true);
            setMobileActionsOpen(false);
          }}
          onOpenTimeline={() => {
            setTimelineSheetOpen(true);
            setMobileActionsOpen(false);
          }}
        />
      )}

      {/* Mobile Tabs at Bottom */}
      {isMobile && (
        <>
          <MobileActionsSheet
            open={mobileActionsOpen}
            onOpenChange={setMobileActionsOpen}
            currentStatus={job.status}
            onStatusChange={handleStatusChange}
            onEditJob={() => {
              setEditName(job.owner_name);
              setEditAddress(job.property_address);
              setEditType(job.property_type);
              setEditLat(job.latitude);
              setEditLng(job.longitude);
              setEditOwnerEmail(job.owner_email || "");
              setEditOwnerPhone(job.owner_phone || "");
              setEditInsuranceCompany(job.insurance_company || "");
              setEditClaimNumber(job.claim_number || "");
              setEditAdjusterName(job.adjuster_name || "");
              setEditAdjusterPhone(job.adjuster_phone || "");
              setEditAdjusterEmail(job.adjuster_email || "");
              setEditPolicyNumber(job.policy_number || "");
              setEditRoofType(job.roof_type || "");
              setEditRoofAge(job.roof_age?.toString() || "");
              setEditDialogOpen(true);
              setMobileActionsOpen(false);
            }}
            onDuplicateJob={() => {
              toast.info("Duplicate job feature coming soon");
              setMobileActionsOpen(false);
            }}
            onArchiveJob={() => {
              toast.info("Archive job feature coming soon");
              setMobileActionsOpen(false);
            }}
            onOpenDocuments={() => {
              setDocumentsSheetOpen(true);
              setMobileActionsOpen(false);
            }}
            onOpenTasks={() => {
              setTasksSheetOpen(true);
              setMobileActionsOpen(false);
            }}
            onOpenPayments={() => {
              setPaymentsSheetOpen(true);
              setMobileActionsOpen(false);
            }}
            onOpenTimeline={() => {
              setTimelineSheetOpen(true);
              setMobileActionsOpen(false);
            }}
          />
          <MobileJobTabs
            activeTab={activeTab}
            onTabChange={setActiveTab}
            jobType={job.property_type}
          />
        </>
      )}
    </Layout>
  );
};

export default JobDetail;
